import * as React from "react";
import { cn } from "../../../helpers";
import CheckIcon from "../../icons/Check";
import styles from "./Dropdown.module.scss";

interface DropdownContextType {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    toggle: () => void;
    triggerOnHover: boolean;
}
const DropdownContext = React.createContext<DropdownContextType | null>(null);

function useDropdownContext() {
    const ctx = React.useContext(DropdownContext);
    if (!ctx) throw new Error("Dropdown components must be used within <Dropdown>");
    return ctx;
}

interface DropdownRootProps {
    children: React.ReactNode;
    triggerOnHover?: boolean;
}

const DropdownRoot = ({ children, triggerOnHover = false }: DropdownRootProps) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const toggle = () => setIsOpen((prev) => !prev);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const closeTimeoutRef = React.useRef<NodeJS.Timeout>();

    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleMouseLeave = () => {
        if (triggerOnHover) {
            closeTimeoutRef.current = setTimeout(() => {
                setIsOpen(false);
            }, 150);
        }
    };

    const handleMouseEnter = () => {
        if (triggerOnHover && closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
    };

    React.useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    return (
        <DropdownContext.Provider
            value={{ isOpen, setIsOpen, toggle, triggerOnHover }}
        >
            <div
                ref={dropdownRef}
                className={styles.dropdown}
                onMouseLeave={handleMouseLeave}
                onMouseEnter={handleMouseEnter}
            >
                {children}
            </div>
        </DropdownContext.Provider>
    );
};

const DropdownTrigger = React.forwardRef<
    HTMLElement,
    { asChild?: boolean; children: React.ReactElement }
>(({ children, asChild = false }, forwardedRef) => {
    const { toggle, triggerOnHover, setIsOpen } = useDropdownContext();

    const handleMouseEnter = () => {
        if (triggerOnHover) {
            setIsOpen(true);
        }
    };

    if (asChild) {
        return React.cloneElement(children, {
            ref: forwardedRef,
            onClick: (e: React.MouseEvent) => {
                if (!triggerOnHover) {
                    children.props.onClick?.(e);
                    toggle();
                }
            },
            onMouseEnter: handleMouseEnter,
        });
    }

    return (
        <button
            className={styles.trigger}
            ref={forwardedRef as React.Ref<HTMLButtonElement>}
            onClick={() => {
                if (!triggerOnHover) {
                    toggle();
                }
            }}
            onMouseEnter={handleMouseEnter}
        >
            {children}
        </button>
    );
});
DropdownTrigger.displayName = "DropdownTrigger";

interface DropdownContentProps {
    children: React.ReactNode;
    className?: string;
    width?: string | number;
    maxHeight?: string | number;
    variant?: "normal" | "primary";
    align?: "start" | "end";
    position?: "top" | "bottom";
}
const DropdownContent = ({
    children,
    className,
    width = "auto",
    maxHeight = "300px",
    variant = "normal",
    align = "start",
    position = "bottom",
}: DropdownContentProps) => {
    const { isOpen, setIsOpen, triggerOnHover } = useDropdownContext();

    if (!isOpen) return null;

    const handleMouseLeave = () => {
        if (triggerOnHover) {
            setIsOpen(false);
        }
    };

    return (
        <div
            className={cn(
                styles.content,
                styles[variant],
                align === "start" ? styles.alignStart : styles.alignEnd,
                className,
                position === "top" ? styles.top : styles.bottom
            )}
            style={{ width, maxHeight }}
            onMouseLeave={handleMouseLeave}
        >
            {children}
        </div>
    );
};

interface DropdownItemProps extends React.HTMLAttributes<HTMLDivElement> {
    disabled?: boolean;
    isSelected?: boolean;
    isMulti?: boolean;
    itemValue?: string;
    values?: string[];
    closeMenuOnSelect?: boolean;
}
const DropdownItem = ({
    children,
    disabled,
    isSelected,
    isMulti,
    itemValue,
    values,
    closeMenuOnSelect = true,
    onClick,
    className,
    ...props
}: DropdownItemProps) => {
    const { setIsOpen } = useDropdownContext();

    const handleClick = (e) => {
        e.stopPropagation();
        onClick?.(e);
        if (!isMulti && closeMenuOnSelect) {
            setIsOpen(false);
        }
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClick?.(e as any);
                    if (!isMulti && closeMenuOnSelect) {
                        setIsOpen(false);
                    }
                }
            }}
            className={cn(
                styles.item,
                isSelected && !isMulti && styles.selected,
                disabled && styles.disabled,
                className
            )}
            {...props}
        >
            {isMulti ? (
                <span className={styles.itemContent}>
                    <span
                        className={cn(
                            styles.multiSelectIcon,
                            itemValue && values?.includes(itemValue)
                                ? styles.active
                                : styles.inactive
                        )}
                    >
                        <CheckIcon />
                    </span>
                    {children}
                </span>
            ) : (
                <span className={styles.itemContent}>
                    {children}
                    {isSelected && (
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="ml-auto"
                        >
                            <g clipPath="url(#clip0)">
                                <path
                                    opacity="0.4"
                                    d="M11.8242 3.38482L6.20508 9.03513L4.5 7.3263L10.1276 1.67786C10.1828 1.62213 10.2485 1.57783 10.3209 1.5475C10.3933 1.51718 10.471 1.50141 10.5495 1.5011C10.628 1.5008 10.7057 1.51596 10.7784 1.54573C10.851 1.5755 10.917 1.61928 10.9727 1.67458L10.976 1.67786L11.8242 2.53122C11.9368 2.64474 12 2.79814 12 2.95802C12 3.1179 11.9368 3.2713 11.8242 3.38482Z"
                                    fill="#1a738d"
                                />
                                <path
                                    d="M6.20325 9.03726L4.92426 10.3233C4.81262 10.4358 4.66086 10.4993 4.50237 10.4999C4.34387 10.5006 4.19162 10.4382 4.07911 10.3266L4.07583 10.3233L0.175827 6.40077C0.0632029 6.28733 0 6.13395 0 5.9741C0 5.81424 0.0632029 5.66086 0.175827 5.54742L1.02426 4.69406C1.13573 4.58183 1.28715 4.51839 1.44532 4.51765C1.60349 4.5169 1.7555 4.57891 1.86801 4.69007L1.87176 4.69406L6.20325 9.03726Z"
                                    fill="#1a738d"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0">
                                    <rect width="12" height="12" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    )}
                </span>
            )}
        </div>
    );
};

const DropdownSeparator = () => (
    <div role="separator" className={styles.separator} />
);

export const Dropdown = Object.assign(DropdownRoot, {
    Trigger: DropdownTrigger,
    Content: DropdownContent,
    Item: DropdownItem,
    Separator: DropdownSeparator,
});
